import { zodResolver } from '@hookform/resolvers/zod';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Loader2,
  Plug,
  RefreshCw,
  Trash2,
  XCircle,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { QueryKey } from '@/Config/api/queryKey';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import { Label } from '@/Domain/components/Shared/Label/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Domain/components/Shared/Select/Select';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import { Switch } from '@/Domain/components/Shared/Switch/Switch';
import CloseIcon from '@/Domain/components/icons/Close.icon';
import { SCENARIO_ID } from '@/context';

const INTERCOM_NS_PREFIX = 'integration:intercom';

// Public Intercom OAuth app identifier (mirrors settings.INTERCOM_CLIENT_ID in Django).
// Not a secret — the client_secret is held server-side and never exposed to the browser.
const INTERCOM_CLIENT_ID = 'c4be8e81-aaa7-4d60-ba62-8545df9748a3';

const INTERCOM_INTEGRATION_NAME = 'intercom' as const;
const INTERCOM_QUERY_KEY = [QueryKey.integration.get, INTERCOM_INTEGRATION_NAME] as const;
const AUTO_CLOSE_QUERY_KEY = [QueryKey.integration.autoCloseMinutes] as const;

const AUTO_CLOSE_VALUES = [null, 5, 10, 30] as const;
type AutoCloseValue = (typeof AUTO_CLOSE_VALUES)[number];

// "null" isn't a valid select value in Radix; use this sentinel to represent it.
const AUTO_CLOSE_NEVER_SENTINEL = '__never__';
const POPUP_POLL_INTERVAL_MS = 500;

// Fields on the `params` object must match IntercomIntegrationParams in
// backend/appquickchat/appquickchat/api/integrations/integration_params_schemas.py.
interface IntercomIntegrationParams {
  intercom_bot_active: boolean;
  intercom_verify_token: string | null;
  intercom_access_token: string | null;
  intercom_app_id: string | null;
  intercom_admin_id: string | null;
  intercom_admin_name: string | null;
  intercom_handoff: boolean;
  intercom_only_admin: boolean;
}

// The wire shape from GET /v1/api/integration/intercom is the standard
// IntegrationData wrapper (see appquickchat/api/integrations/schemas.py).
interface IntercomIntegrationResponse {
  name: typeof INTERCOM_INTEGRATION_NAME;
  params: IntercomIntegrationParams;
}

interface AutoCloseResponse {
  auto_close_minutes: AutoCloseValue;
  valid_values: AutoCloseValue[];
}

interface ValidateTokenResponse {
  status: 'ok' | 'error';
  admin_name?: string;
  message?: string;
}

type IntercomFormValues = {
  intercom_bot_active: boolean;
  intercom_handoff: boolean;
  // UI field: when true (on), `intercom_only_admin` is sent as false.
  reply_to_all: boolean;
  auto_close: string;
};

const formSchema = z.object({
  intercom_bot_active: z.boolean(),
  intercom_handoff: z.boolean(),
  reply_to_all: z.boolean(),
  auto_close: z.string(),
});

const autoCloseOptionKey = (value: AutoCloseValue): string =>
  value === null ? AUTO_CLOSE_NEVER_SENTINEL : String(value);

const parseAutoCloseKey = (key: string): AutoCloseValue => {
  if (key === AUTO_CLOSE_NEVER_SENTINEL) return null;
  const num = Number(key);
  return (AUTO_CLOSE_VALUES as readonly AutoCloseValue[]).includes(num as AutoCloseValue)
    ? (num as AutoCloseValue)
    : null;
};

const buildOAuthUrl = (clientId: string, verifyToken: string): string => {
  const state = encodeURIComponent(
    JSON.stringify({ scenario_id: SCENARIO_ID, verify_token: verifyToken }),
  );
  return `https://app.intercom.com/oauth?client_id=${clientId}&state=${state}`;
};

interface IntercomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const IntercomSheet: React.FC<IntercomSheetProps> = ({ open, onOpenChange }) => {
  const { t } = useTranslation(['integration']);
  const queryClient = useQueryClient();
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const popupPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearPopupPoll = useCallback(() => {
    if (popupPollRef.current !== null) {
      clearInterval(popupPollRef.current);
      popupPollRef.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      clearPopupPoll();
    },
    [clearPopupPoll],
  );

  const integrationQuery = useQuery<IntercomIntegrationResponse>({
    queryKey: INTERCOM_QUERY_KEY,
    enabled: open,
  });

  const autoCloseQuery = useQuery<AutoCloseResponse>({
    queryKey: AUTO_CLOSE_QUERY_KEY,
    enabled: open,
  });

  const integrationData = integrationQuery.data?.params;
  const isConnected = Boolean(integrationData?.intercom_access_token);

  const patchIntegrationMutation = useMutation<
    unknown,
    Error,
    {
      name: typeof INTERCOM_INTEGRATION_NAME;
      params: {
        intercom_bot_active: boolean;
        intercom_handoff: boolean;
        intercom_only_admin: boolean;
      };
    }
  >({
    mutationKey: [QueryKey.integration.patch],
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTERCOM_QUERY_KEY });
      toast.success(t(`${INTERCOM_NS_PREFIX}.saved`));
    },
    onError: () => {
      toast.error(t(`${INTERCOM_NS_PREFIX}.save_failed`));
    },
  });

  const patchAutoCloseMutation = useMutation<unknown, Error, AutoCloseValue>({
    mutationKey: [QueryKey.integration.autoCloseMinutesPatch],
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AUTO_CLOSE_QUERY_KEY });
    },
    onError: () => {
      toast.error(t(`${INTERCOM_NS_PREFIX}.save_failed`));
    },
  });

  const validateTokenMutation = useMutation<ValidateTokenResponse>({
    mutationKey: [QueryKey.integration.intercomValidateToken],
    onSuccess: (data) => {
      if (data.status === 'ok') {
        toast.success(t(`${INTERCOM_NS_PREFIX}.test_succeeded`, { name: data.admin_name || '' }));
      } else {
        toast.error(t(`${INTERCOM_NS_PREFIX}.test_failed`, { message: data.message || '' }));
      }
    },
    onError: () => {
      toast.error(t(`${INTERCOM_NS_PREFIX}.test_failed`, { message: '' }));
    },
  });

  const removeMutation = useMutation({
    mutationKey: [QueryKey.integration.intercomRemove],
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTERCOM_QUERY_KEY });
      setConfirmRemoveOpen(false);
    },
    onError: () => {
      toast.error(t(`${INTERCOM_NS_PREFIX}.remove_failed`));
    },
  });

  const defaultValues: IntercomFormValues = useMemo(
    () => ({
      intercom_bot_active: integrationData?.intercom_bot_active ?? false,
      intercom_handoff: integrationData?.intercom_handoff ?? false,
      // Invert for UI: "Reply to all" on == intercom_only_admin false.
      reply_to_all: integrationData ? !integrationData.intercom_only_admin : true,
      auto_close: autoCloseOptionKey(autoCloseQuery.data?.auto_close_minutes ?? null),
    }),
    [integrationData, autoCloseQuery.data],
  );

  const { control, handleSubmit, reset } = useForm<IntercomFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  // Reset form whenever the server-returned values change (after fetch or invalidation).
  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const onSubmit = handleSubmit((values) => {
    patchIntegrationMutation.mutate({
      name: INTERCOM_INTEGRATION_NAME,
      params: {
        intercom_bot_active: values.intercom_bot_active,
        intercom_handoff: values.intercom_handoff,
        // UI toggle is inverted vs. backend field.
        intercom_only_admin: !values.reply_to_all,
      },
    });
    const nextAutoClose = parseAutoCloseKey(values.auto_close);
    if (nextAutoClose !== (autoCloseQuery.data?.auto_close_minutes ?? null)) {
      patchAutoCloseMutation.mutate(nextAutoClose);
    }
  });

  const handleAuthenticate = useCallback(() => {
    const verifyToken = integrationData?.intercom_verify_token;
    if (!verifyToken) {
      toast.error(t(`${INTERCOM_NS_PREFIX}.oauth_failed`));
      return;
    }
    const url = buildOAuthUrl(INTERCOM_CLIENT_ID, verifyToken);
    const popup = window.open(url, 'intercom_oauth', 'width=600,height=700');
    if (!popup) {
      toast.error(t(`${INTERCOM_NS_PREFIX}.popup_blocked`));
      return;
    }

    clearPopupPoll();
    popupPollRef.current = setInterval(() => {
      if (typeof window === 'undefined' || !popup) {
        clearPopupPoll();
        return;
      }
      if (popup.closed) {
        clearPopupPoll();
        queryClient.invalidateQueries({ queryKey: INTERCOM_QUERY_KEY });
      }
    }, POPUP_POLL_INTERVAL_MS);
  }, [integrationData, t, clearPopupPoll, queryClient]);

  const handleTestConnection = useCallback(() => {
    validateTokenMutation.mutate();
  }, [validateTokenMutation]);

  const handleRemove = useCallback(() => {
    removeMutation.mutate();
  }, [removeMutation]);

  const isLoading = integrationQuery.isLoading || autoCloseQuery.isLoading;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="max-w-3xl w-full h-full"
        tabIndex={undefined}
        data-testid="intercom-sheet"
      >
        <SheetHeader className="px-0.5">
          <SheetTitle data-testid="intercom-sheet-title">
            {t(`${INTERCOM_NS_PREFIX}.title`)}
          </SheetTitle>
          <IconButton
            onClick={() => onOpenChange(false)}
            variant="secondary"
            data-testid="intercom-close-button"
          >
            <CloseIcon />
          </IconButton>
        </SheetHeader>

        <p className="text-sm text-muted-foreground px-0.5" data-testid="intercom-description">
          {t(`${INTERCOM_NS_PREFIX}.description`)}
        </p>

        <div className="overflow-y-auto px-0.5 space-y-6 flex-1">
          {isLoading ? (
            <div className="flex items-center justify-center py-12" data-testid="intercom-loading">
              <Loader2 className="size-6 animate-spin text-foreground-subtle" />
            </div>
          ) : !isConnected ? (
            <div
              className="flex flex-col items-center justify-center gap-4 py-12 border border-dashed border-border rounded-xl"
              data-testid="intercom-not-connected"
            >
              <Plug className="size-10 text-foreground-subtle" />
              <p className="text-sm text-muted-foreground text-center max-w-sm">
                {t(`${INTERCOM_NS_PREFIX}.description`)}
              </p>
              <Button
                variant="dark"
                onClick={handleAuthenticate}
                data-testid="intercom-authenticate-button"
              >
                <ExternalLink className="size-4" />
                {t(`${INTERCOM_NS_PREFIX}.authenticate`)}
              </Button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-6" data-testid="intercom-form">
              <div
                className="rounded-xl border border-border bg-muted p-4 space-y-3"
                data-testid="intercom-connection-card"
              >
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="size-5 text-success" />
                  <span
                    className="text-sm font-medium text-foreground"
                    data-testid="intercom-connected-as"
                  >
                    {t(`${INTERCOM_NS_PREFIX}.connected_as`, {
                      name: integrationData?.intercom_admin_name || '',
                    })}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <Label className="text-xs text-foreground-subtle font-normal">
                      {t(`${INTERCOM_NS_PREFIX}.app_id`)}
                    </Label>
                    <p className="text-sm text-foreground font-mono" data-testid="intercom-app-id">
                      {integrationData?.intercom_app_id || '-'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-foreground-subtle font-normal">
                      {t(`${INTERCOM_NS_PREFIX}.admin_id`)}
                    </Label>
                    <p
                      className="text-sm text-foreground font-mono"
                      data-testid="intercom-admin-id"
                    >
                      {integrationData?.intercom_admin_id || '-'}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 pt-2">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={handleAuthenticate}
                    data-testid="intercom-reauthenticate-button"
                  >
                    <RefreshCw className="size-4" />
                    {t(`${INTERCOM_NS_PREFIX}.reauthenticate`)}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={handleTestConnection}
                    disabled={validateTokenMutation.isPending}
                    data-testid="intercom-test-connection-button"
                  >
                    {validateTokenMutation.isPending ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <CheckCircle2 className="size-4" />
                    )}
                    {t(`${INTERCOM_NS_PREFIX}.test_connection`)}
                  </Button>
                  <Button
                    type="button"
                    variant="danger"
                    size="sm"
                    onClick={() => setConfirmRemoveOpen(true)}
                    data-testid="intercom-remove-button"
                  >
                    <Trash2 className="size-4" />
                    {t(`${INTERCOM_NS_PREFIX}.remove`)}
                  </Button>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <Label className="text-sm font-medium text-foreground">
                      {t(`${INTERCOM_NS_PREFIX}.active.label`)}
                    </Label>
                  </div>
                  <Controller
                    control={control}
                    name="intercom_bot_active"
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        data-testid="intercom-active-toggle"
                      />
                    )}
                  />
                </div>

                <Separator />

                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <Label className="text-sm font-medium text-foreground">
                      {t(`${INTERCOM_NS_PREFIX}.handoff.label`)}
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t(`${INTERCOM_NS_PREFIX}.handoff.description`)}
                    </p>
                  </div>
                  <Controller
                    control={control}
                    name="intercom_handoff"
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        data-testid="intercom-handoff-toggle"
                      />
                    )}
                  />
                </div>

                <Separator />

                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <Label className="text-sm font-medium text-foreground">
                      {t(`${INTERCOM_NS_PREFIX}.reply_to_all.label`)}
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t(`${INTERCOM_NS_PREFIX}.reply_to_all.description`)}
                    </p>
                  </div>
                  <Controller
                    control={control}
                    name="reply_to_all"
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        data-testid="intercom-reply-to-all-toggle"
                      />
                    )}
                  />
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-foreground">
                    {t(`${INTERCOM_NS_PREFIX}.auto_close.label`)}
                  </Label>
                  <Controller
                    control={control}
                    name="auto_close"
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger className="w-full" data-testid="intercom-auto-close-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem
                            value={AUTO_CLOSE_NEVER_SENTINEL}
                            data-testid="intercom-auto-close-option-never"
                          >
                            {t(`${INTERCOM_NS_PREFIX}.auto_close.options.never`)}
                          </SelectItem>
                          <SelectItem value="5" data-testid="intercom-auto-close-option-5">
                            {t(`${INTERCOM_NS_PREFIX}.auto_close.options.5`)}
                          </SelectItem>
                          <SelectItem value="10" data-testid="intercom-auto-close-option-10">
                            {t(`${INTERCOM_NS_PREFIX}.auto_close.options.10`)}
                          </SelectItem>
                          <SelectItem value="30" data-testid="intercom-auto-close-option-30">
                            {t(`${INTERCOM_NS_PREFIX}.auto_close.options.30`)}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
              </div>

              <div className="pt-2">
                <Button
                  type="submit"
                  variant="dark"
                  disabled={patchIntegrationMutation.isPending || patchAutoCloseMutation.isPending}
                  data-testid="intercom-save-button"
                >
                  {(patchIntegrationMutation.isPending || patchAutoCloseMutation.isPending) && (
                    <Loader2 className="size-4 animate-spin" />
                  )}
                  {t(`${INTERCOM_NS_PREFIX}.save`)}
                </Button>
              </div>
            </form>
          )}
        </div>

        <AlertDialogPrimitive.Root open={confirmRemoveOpen} onOpenChange={setConfirmRemoveOpen}>
          <AlertDialogPrimitive.Overlay className="!mt-0 absolute inset-0 bg-black/30 z-20" />
          <AlertDialogPrimitive.Content className="absolute inset-0 flex items-center justify-center p-4 z-30 focus:outline-none">
            <div
              className="space-y-4 bg-card rounded-lg p-6 max-w-md w-full shadow-lg"
              data-testid="intercom-remove-dialog"
            >
              <div className="flex items-center gap-3">
                <AlertTriangle className="size-6 text-destructive" />
                <h2 className="text-lg font-semibold text-foreground">
                  {t(`${INTERCOM_NS_PREFIX}.remove_confirm.title`)}
                </h2>
              </div>
              <p className="text-sm text-muted-foreground">
                {t(`${INTERCOM_NS_PREFIX}.remove_confirm.description`)}
              </p>
              <Separator />
              <div className="flex justify-end gap-2">
                <AlertDialogPrimitive.Cancel asChild>
                  <Button variant="secondary" size="md" data-testid="intercom-remove-cancel-button">
                    {t(`${INTERCOM_NS_PREFIX}.remove_confirm.cancel`)}
                  </Button>
                </AlertDialogPrimitive.Cancel>
                <Button
                  variant="danger"
                  size="md"
                  onClick={handleRemove}
                  disabled={removeMutation.isPending}
                  data-testid="intercom-remove-confirm-button"
                >
                  {removeMutation.isPending ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <XCircle className="size-4" />
                  )}
                  {t(`${INTERCOM_NS_PREFIX}.remove_confirm.confirm`)}
                </Button>
              </div>
            </div>
          </AlertDialogPrimitive.Content>
        </AlertDialogPrimitive.Root>
      </SheetContent>
    </Sheet>
  );
};
